import Phaser from 'phaser';
import { HAPTICS, PALETTE } from '../../config';
import { createAthleteView, type AthleteView } from '../../game/AthleteView';
import { AudioEngine } from '../../game/AudioEngine';
import { getDiscipline } from '../../game/data/disciplines';
import { getSkin } from '../../game/data/skins';
import { countryFlag } from '../../game/data/flags';
import { pickOpponents } from '../../game/data/aiRoster';
import { t } from '../../game/i18n';
import type { DisciplineResult, Standing } from '../../game/types';
import { Button } from '../../ui/Button';
import { viewport } from '../../ui/viewport';
import { DisciplineScene } from './DisciplineScene';

type Phase = 'intro' | 'aim' | 'between' | 'done';

const ARROWS = 3;
const RING_COLORS = [0xffd24a, 0xff5d6c, 0x59d2ff, 0x2a3358, 0xf4f7ff];

/**
 * Archery — the reticle drifts (sway + wind); tap LANCIA to loose the arrow. Closer to the
 * gold = more points (10 down to 1, miss = 0). Three arrows, best total wins; beat your own
 * total for a record. Steady aim, no mashing.
 */
export class Archery extends DisciplineScene {
  private phase: Phase = 'intro';
  private rig!: AthleteView;

  private arrow = 0;
  private total = 0;
  private windX = 0;
  private steadiness = 0.6;
  private tx = 0; private ty = 0; private R = 0;
  private rx = 0; private ry = 0;

  private W = 0; private H = 0; private uiS = 1;
  private aiTotals: { name: string; species: string; nationality: string; metric: number }[] = [];

  private reticle!: Phaser.GameObjects.Graphics;
  private hud!: Phaser.GameObjects.Text;
  private bigText!: Phaser.GameObjects.Text;
  private windText!: Phaser.GameObjects.Text;
  private shootBtn!: Button;

  constructor() { super('Archery'); }

  create(): void {
    super.create();
    this.phase = 'intro';
    this.arrow = 0; this.total = 0; this.rx = 0; this.ry = 0;

    const { w, h, s } = viewport(this);
    this.W = w; this.H = h; this.uiS = s;
    this.tx = w * 0.7; this.ty = h * 0.44; this.R = Math.min(w, h) * 0.19;
    // Higher agility/technique = steadier aim (less sway).
    this.steadiness = 0.45 + ((this.athlete.stats.agility + this.athlete.stats.technique) / 200) * 0.4;

    this.add.graphics().setDepth(-10).fillStyle(0x143a2a, 1).fillRect(0, 0, w, h)
      .fillStyle(0x1d5038, 1).fillRect(0, h * 0.7, w, h * 0.3);
    this.drawTarget(false);
    this.rig = createAthleteView(this, w * 0.2, h * 0.78, this.athlete, Phaser.Math.Clamp(h * 0.4, 180, 340));
    this.rig.container.setDepth(6);
    this.reticle = this.add.graphics().setDepth(20);

    const opponents = pickOpponents(getSkin(this.athlete.skin).id, 3);
    this.aiTotals = opponents.map((o) => ({ name: o.name, species: o.species, nationality: o.nationality, metric: Math.round(Math.max(8, 16 + (o.jumpDistance - 5) * 1.4 + (Math.random() * 8 - 4))) }));

    this.buildHud();
    this.controls.on('action', () => this.shoot());
    this.controls.on('tapLeft', () => this.shoot());
    this.controls.on('tapRight', () => this.shoot());
    this.startCountdown();
  }

  private drawTarget(dim: boolean): void {
    const g = this.add.graphics().setDepth(-5);
    const a = dim ? 0.5 : 1;
    for (let i = 0; i < 5; i++) {
      g.fillStyle(RING_COLORS[i]!, a).fillCircle(this.tx, this.ty, this.R * (1 - i * 0.18));
    }
    g.lineStyle(2, PALETTE.ink, 0.5).strokeCircle(this.tx, this.ty, this.R);
  }

  private buildHud(): void {
    const fs = (n: number) => `${Math.max(13, Math.round(n * this.uiS))}px`;
    this.hud = this.add.text(this.W / 2, 16, '', { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(24), color: '#f4f7ff', fontStyle: 'bold' })
      .setOrigin(0.5, 0).setScrollFactor(0).setShadow(0, 3, '#000000aa', 5);
    this.windText = this.add.text(this.W / 2, 16 + Math.max(26, 30 * this.uiS), '', { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(15), color: '#bfe6ff' })
      .setOrigin(0.5, 0).setScrollFactor(0);
    this.bigText = this.add.text(this.tx, this.ty, '', { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(64), color: '#ffd24a', fontStyle: 'bold' })
      .setOrigin(0.5).setShadow(0, 8, '#00000088', 12).setDepth(25);
    const label = this.sys.game.device.os.desktop ? `🏹 ${t('btn.shoot')} ${t('btn.space')}` : `🏹 ${t('btn.shoot')}`;
    this.shootBtn = new Button(this, this.W / 2, this.H - Math.max(30, 38 * this.uiS), label, () => this.shoot(), {
      width: Math.min(220, this.W * 0.45), height: Math.max(52, 58 * this.uiS), fontSize: Math.max(18, Math.round(22 * this.uiS)), color: PALETTE.laneAccent,
    });
    this.shootBtn.setScrollFactor(0).setAlpha(0.92).setVisible(false);
  }

  private startCountdown(): void {
    ['3', '2', '1', t('game.go')].forEach((label, i) => {
      this.time.delayedCall(i * 500, () => {
        if (label === t('game.go')) { this.shootBtn.setVisible(true); this.nextArrow(); }
      });
    });
  }

  private nextArrow(): void {
    if (this.arrow >= ARROWS) { this.finish(); return; }
    this.phase = 'aim';
    this.windX = (Math.random() * 2 - 1) * this.R * 0.5;
    this.controls.setEnabled(true);
    const dir = this.windX < 0 ? '←' : '→';
    this.windText.setText(t('game.wind', { dir, n: Math.abs(this.windX / this.R * 20).toFixed(0) }));
    this.hud.setText(t('game.arrowHud', { n: this.arrow + 1, max: ARROWS, p: this.total }));
  }

  private shoot(): void {
    if (this.phase !== 'aim') return;
    this.phase = 'between';
    this.controls.setEnabled(false);
    const dist = Math.hypot(this.rx, this.ry);
    const ring = Phaser.Math.Clamp(Math.round(10 - (dist / this.R) * 10), 0, 10);
    this.total += ring;
    // Stick the arrow where it landed.
    this.add.graphics().setDepth(22).fillStyle(0x2a3358, 1).fillCircle(this.tx + this.rx, this.ty + this.ry, 4)
      .lineStyle(2, 0xffffff, 0.8).strokeCircle(this.tx + this.rx, this.ty + this.ry, 4);
    AudioEngine.go();
    this.vibrate(ring >= 9 ? HAPTICS.goodTap : HAPTICS.tap);
    this.bigText.setText(ring === 0 ? t('game.out') : `${ring}`).setColor(ring >= 9 ? '#4ade80' : ring === 0 ? '#ff5d6c' : '#ffd24a').setAlpha(1).setScale(1.6);
    this.tweens.add({ targets: this.bigText, scale: 1, duration: 300, ease: 'Back.out' });
    this.arrow++;
    this.time.delayedCall(1100, () => { this.bigText.setAlpha(0); this.nextArrow(); });
  }

  update(_t: number, delta: number): void {
    const dt = delta / 1000;
    this.rig.update(dt);
    if (this.phase !== 'aim') return;
    // Lissajous sway (two frequencies) + wind; amplitude shrinks with steadiness.
    const t = this.time.now / 1000;
    const amp = this.R * (1 - this.steadiness) * 0.95;
    this.rx = this.windX + amp * Math.sin(t * 2.3);
    this.ry = amp * 0.8 * Math.sin(t * 3.1 + 1.2);
    this.drawReticle();
  }

  private drawReticle(): void {
    const g = this.reticle;
    g.clear();
    const x = this.tx + this.rx, y = this.ty + this.ry;
    g.lineStyle(2.5, 0x0b1020, 0.9);
    g.strokeCircle(x, y, 14);
    g.beginPath(); g.moveTo(x - 20, y); g.lineTo(x - 6, y); g.moveTo(x + 6, y); g.lineTo(x + 20, y);
    g.moveTo(x, y - 20); g.lineTo(x, y - 6); g.moveTo(x, y + 6); g.lineTo(x, y + 20); g.strokePath();
    g.fillStyle(0xff5d6c, 1).fillCircle(x, y, 3);
  }

  private finish(): void {
    this.phase = 'done';
    this.controls.setEnabled(false);
    this.reticle.clear();
    this.shootBtn.setVisible(false);

    const standings: Standing[] = [
      { name: this.athlete.name, species: this.athlete.species, isPlayer: true, nationality: this.athlete.nationality, metric: this.total },
      ...this.aiTotals.map((a) => ({ name: a.name, species: a.species, isPlayer: false, nationality: a.nationality, metric: a.metric })),
    ].sort((x, y) => y.metric - x.metric);
    const rank = standings.findIndex((s) => s.isPlayer) + 1;
    const score = Math.round(Phaser.Math.Clamp((this.total / (ARROWS * 10)) * 1000, 0, 1000));
    const reward = getDiscipline('archery').scoreToReward(score);

    this.hud.setText('');
    this.windText.setText('');
    this.bigText.setText(`${this.total} pt`).setColor('#ffd24a').setAlpha(1).setScale(0.6).setScrollFactor(0).setPosition(this.W / 2, this.H * 0.4);
    this.tweens.add({ targets: this.bigText, scale: 1, duration: 500, ease: 'Back.out' });
    AudioEngine.fanfare();
    this.add.text(this.tx, this.ty + this.R + 24, `${countryFlag(this.athlete.nationality)} ${this.total} pt`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(14 * this.uiS)}px`, color: '#0b1020', fontStyle: 'bold', backgroundColor: '#ffd24a', padding: { x: 6, y: 3 },
    }).setOrigin(0.5, 0).setDepth(9);

    const result: DisciplineResult = {
      disciplineId: 'archery', metricKind: 'distance', athleteId: this.athlete.id, athleteName: this.athlete.name,
      species: this.athlete.species, nationality: this.athlete.nationality, metric: this.total, score, reward, rank, standings,
    };
    this.time.delayedCall(2400, () => this.finishDiscipline(result));
  }

  private vibrate(pattern: number | number[]): void {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try { navigator.vibrate(pattern); } catch { /* unsupported */ }
    }
  }
}
